import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { node } from 'prop-types';
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { auth, googleProvider, hasConfig } from './firebaseClient';

const AuthContext = createContext(null);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hasConfig || !auth) {
      setLoading(false);
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    hasConfig,
    signInWithEmail: async (email, password) => {
      if (!hasConfig || !auth) {
        throw new Error('Firebase auth is not configured.');
      }
      return signInWithEmailAndPassword(auth, email, password);
    },
    signUpWithEmail: async (email, password) => {
      if (!hasConfig || !auth) {
        throw new Error('Firebase auth is not configured.');
      }
      return createUserWithEmailAndPassword(auth, email, password);
    },
    signInWithGoogle: async () => {
      if (!hasConfig || !auth || !googleProvider) {
        throw new Error('Google auth is not configured.');
      }
      return signInWithPopup(auth, googleProvider);
    },
    signOutUser: async () => {
      if (!hasConfig || !auth) return;
      await signOut(auth);
    },
  }), [loading, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: node.isRequired,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider.');
  }
  return context;
};

export default AuthProvider;
